import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { api } from '../../api';
import { ortOf } from '../KundeDaten';
import { ImportKnopf } from '../KundenList';
import { darf, fehlerText, MitRecht, NurAnsehen } from '../tarifHinweis';
import type { Overview } from '../types';
import { urlId } from '../urlId';
import { KontakteNav } from './KontakteNav';
import { dateienZipUrl, gleichesWort, passtZuKontakt } from './KontaktDaten';
import { LieferantDetail } from './LieferantDetail';
import { lieferantenText, lieferantenUrl, type LieferantenAntwort, type LieferantZeile } from './LieferantDaten';
import { LieferantForm } from './LieferantForm';
import { MitVorschlaegen, SchlagwoerterDialog, SchlagwortChips, SchlagwortFilter } from './Schlagwoerter';
import '../kunden.css';
import './kontakte.css';

type Sort = 'name' | 'nummer';
const SEITE = 60;

interface ListeZustand {
  q: string;
  schlagwort: string;
  sort: Sort;
}

// Lieferanten (nur Billtoro): /rechnung/lieferanten (Liste), /neu, /:id (ein Lieferant), /:id/bearbeiten. Suche,
// Schlagwort und Sortierung bleiben beim Zurückkommen erhalten. Ändern nur mit dem Recht „Kontakte, Leistungen und Lager“.
// „Importieren“ führt zum Import der Kunden (/rechnung/kunden/import), der Lieferanten der Datei mit anlegt.
export function LieferantenList({ data }: { data: Overview }) {
  const [liste, setListe] = useState<LieferantenAntwort | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [zustand, setZustand] = useState<ListeZustand>({ q: '', schlagwort: '', sort: 'name' });
  const firmId = data.firm.id;
  const aktuell = useRef(firmId);
  aktuell.current = firmId;

  const laden = useCallback(async () => {
    try {
      const res = await api.get<LieferantenAntwort>(lieferantenUrl(firmId));
      // inzwischen andere Firma gewählt: Antwort verwerfen
      if (aktuell.current !== firmId) return;
      setListe(res);
      setError(null);
    } catch (err) {
      if (aktuell.current === firmId) setError(fehlerText(err));
    }
  }, [firmId]);

  // andere Firma gewählt: Liste und Auswahl neu
  useEffect(() => {
    setListe(null);
    setZustand({ q: '', schlagwort: '', sort: 'name' });
  }, [firmId]);

  const vorschlaege = liste?.schlagwoerter.map((w) => w.wort) ?? [];

  // Formulare nur mit dem Recht; die Liste (Vorschläge für „Schlagwörter“) auch beim direkten Öffnen laden
  const recht = (form: ReactNode) => (
    <MitRecht tarif={data.tarif} bereich="kunden" zu="/rechnung/lieferanten">
      <MitVorschlaegen da={!!liste} laden={laden}>
        {form}
      </MitVorschlaegen>
    </MitRecht>
  );
  return (
    <Routes>
      <Route index element={<Liste data={data} liste={liste} error={error} laden={laden} zustand={zustand} setZustand={setZustand} />} />
      <Route path="neu" element={recht(<LieferantForm data={data} vorschlaege={vorschlaege} onSaved={laden} />)} />
      <Route
        path=":id"
        element={
          <NurMitId>
            <LieferantDetail data={data} onChanged={laden} />
          </NurMitId>
        }
      />
      <Route
        path=":id/bearbeiten"
        element={recht(
          <NurMitId>
            <Bearbeiten data={data} vorschlaege={vorschlaege} onSaved={laden} />
          </NurMitId>,
        )}
      />
      <Route path="*" element={<Navigate to="/rechnung/lieferanten" replace />} />
    </Routes>
  );
}

// Lieferant aus der Adresse geht in API-Pfade: nur mit echter ID, sonst zur Liste (urlId.ts)
function NurMitId({ children }: { children: ReactNode }) {
  const { id } = useParams();
  return urlId(id) ? children : <Navigate to="/rechnung/lieferanten" replace />;
}

function Bearbeiten({ data, vorschlaege, onSaved }: { data: Overview; vorschlaege: string[]; onSaved(): Promise<void> }) {
  const { id } = useParams();
  return <LieferantForm key={id} data={data} id={id} vorschlaege={vorschlaege} onSaved={onSaved} />;
}

// Suche: Name (auch Zusatz), Nummer, PLZ und Ort, E-Mail, unsere Kundennummer dort, Schlagwörter, Ansprechpartner
function passt(l: LieferantZeile, q: string) {
  const t = q.trim().toLowerCase();
  if (!t) return true;
  if (/^\d+$/.test(t) && String(l.nummer).startsWith(t)) return true;
  return (
    [l.name, l.extra, `${l.zip} ${l.city}`, l.email, l.kundennummerDort].some((v) => v?.toLowerCase().includes(t)) || passtZuKontakt(l, t)
  );
}

const sortiert = (list: LieferantZeile[], sort: Sort) =>
  [...list].sort((a, b) =>
    sort === 'nummer' ? a.nummer - b.nummer : a.name.localeCompare(b.name, 'de', { sensitivity: 'base' }) || a.nummer - b.nummer,
  );

function Liste({
  data,
  liste,
  error,
  laden,
  zustand,
  setZustand,
}: {
  data: Overview;
  liste: LieferantenAntwort | null;
  error: string | null;
  laden(): Promise<void>;
  zustand: ListeZustand;
  setZustand(z: ListeZustand): void;
}) {
  const [mehr, setMehr] = useState(SEITE);
  const [verwalten, setVerwalten] = useState(false);
  const { q, schlagwort, sort } = zustand;
  // Rechte des Logins (nur Anzeige, der Server prüft)
  const pflegen = darf(data.tarif, 'kunden');
  const exportieren = darf(data.tarif, 'export');
  // Billtoro: alle Dateien an Kunden und Lieferanten als ZIP (neben den Chips)
  const zip = exportieren ? dateienZipUrl(data.firm.id) : undefined;
  const set = (z: Partial<ListeZustand>) => {
    setZustand({ ...zustand, ...z });
    setMehr(SEITE);
  };

  // bei jedem Besuch der Liste neu laden
  useEffect(() => {
    void laden();
  }, [laden]);

  if (!liste)
    return (
      <>
        <KontakteNav zip={zip} />
        {error ? <div className="notice bad">{error}</div> : <div className="spinner" aria-label="Lädt" />}
      </>
    );
  const alle = liste.lieferanten;
  const shown = sortiert(
    alle.filter((l) => (!schlagwort || l.schlagwoerter.some((w) => gleichesWort(w, schlagwort))) && passt(l, q)),
    sort,
  );

  return (
    <>
      <KontakteNav zip={zip} />
      <div className="rt-head kd-head">
        <div>
          <h1>
            Lieferanten <NurAnsehen tarif={data.tarif} bereich="kunden" />
          </h1>
          <p className="muted">{alle.length ? lieferantenText(alle.length) : 'Noch keine Lieferanten'}</p>
        </div>
        {alle.length > 0 && (pflegen || exportieren) && (
          <div className="rt-head-act">
            {pflegen && (
              <ImportKnopf data={data} to="/rechnung/kunden/import" von="lieferanten">
                Importieren
              </ImportKnopf>
            )}
            {exportieren && (
              <a className="btn" href={`${lieferantenUrl(data.firm.id)}/export`} download>
                Exportieren
              </a>
            )}
            {pflegen && (
              <Link to="neu" className="btn primary kd-neu">
                + Neuer Lieferant
              </Link>
            )}
          </div>
        )}
      </div>

      {alle.length === 0 ? (
        <div className="rt-card rt-empty kd-empty">
          <p>Legen Sie hier Ihre Lieferanten an oder übernehmen Sie sie aus einer Liste (CSV oder Excel).</p>
          {pflegen && (
            <div className="kd-empty-act">
              <ImportKnopf data={data} to="/rechnung/kunden/import" von="lieferanten">
                Lieferanten importieren
              </ImportKnopf>
              <Link to="neu" className="btn primary">
                + Neuer Lieferant
              </Link>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="rt-tools kd-tools">
            <input
              type="search"
              className="rt-search"
              value={q}
              placeholder="Suchen: Name, Nummer, Ort, E-Mail oder Ansprechpartner"
              aria-label="Lieferanten suchen"
              onChange={(e) => set({ q: e.target.value })}
            />
            <SchlagwortFilter woerter={liste.schlagwoerter} art="lieferant" value={schlagwort} onChange={(v) => set({ schlagwort: v })} />
            {pflegen && liste.schlagwoerter.length > 0 && (
              <button type="button" className="linkbtn kt-verwalten" onClick={() => setVerwalten(true)}>
                Schlagwörter verwalten
              </button>
            )}
            <label className="kd-sort">
              <span>Sortieren</span>
              <select value={sort} onChange={(e) => set({ sort: e.target.value as Sort })}>
                <option value="name">Name</option>
                <option value="nummer">Nummer</option>
              </select>
            </label>
          </div>

          {shown.length === 0 ? (
            <div className="rt-card rt-empty">
              <p>Kein Lieferant passt zu dieser Auswahl.</p>
              <button type="button" className="btn small" onClick={() => set({ q: '', schlagwort: '' })}>
                Alle zeigen
              </button>
            </div>
          ) : (
            <>
              <ul className="kd-grid">
                {shown.slice(0, mehr).map((l) => (
                  <li key={l.id}>
                    <Karte l={l} />
                  </li>
                ))}
              </ul>
              {shown.length > mehr && (
                <p className="kd-mehr">
                  <button type="button" className="btn" onClick={() => setMehr(mehr + SEITE * 4)}>
                    Weitere zeigen ({shown.length - mehr})
                  </button>
                </p>
              )}
            </>
          )}
        </>
      )}
      <SchlagwoerterDialog
        open={verwalten}
        firmId={data.firm.id}
        woerter={liste.schlagwoerter}
        onClose={() => setVerwalten(false)}
        onErsetzt={(alt, neu) => schlagwort && gleichesWort(alt, schlagwort) && set({ schlagwort: neu ?? '' })}
        onChanged={laden}
      />
    </>
  );
}

function Karte({ l }: { l: LieferantZeile }) {
  const ort = ortOf(l);
  return (
    <Link to={l.id} className="kd-card kt-card">
      <span className="kd-top">
        <b className="kd-name">{l.name}</b>
        <span className="kd-nr num" aria-label={`Lieferantennummer ${l.nummer}`}>
          {l.nummer}
        </span>
      </span>
      <span className="kd-ort">
        <span>{ort || '–'}</span>
        {l.dateien > 0 && <span className="badge b-none">{l.dateien === 1 ? '1 Datei' : `${l.dateien} Dateien`}</span>}
      </span>
      <SchlagwortChips woerter={l.schlagwoerter} />
    </Link>
  );
}
